import React, { useEffect, useState } from 'react'
import {
  PaginationState,
  Table
} from '@contrast-security-inc/design-system-tables'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { dateFormats } from '../react-intl-helpers'
import { css, cx } from '@emotion/css'
import {
  SearchInput,
  TextInput
} from '@contrast-security-inc/design-system-inputs'
import {
  Button,
  IconButton
} from '@contrast-security-inc/design-system-buttons'
import {
  IconAdd,
  IconDelete
} from '@contrast-security-inc/design-system-foundations'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import { getAgentUserDetailsRoute, updateRoute } from '../route'
import { useOrganizationAgents } from './api/use-agent-keys'
import { RouteContext } from '../route-provider'
import { toast } from '@contrast-security-inc/design-system-alerts'
import { DeleteAgentDialog } from './delete-agent-dialog'
import { Role } from '../roles'
import { RoleSwitch } from 'contrastBase/RoleSwitch'
import { useAddAgentUser } from './api/use-add-agent-user'
import { useQueryClient } from 'react-query'
import { Query } from '../../hooks/queries'
import { readOnlyGridCss } from '../../legacy-design-system/text'
import { useDeleteAgentUser } from './api/use-delete-agent-user'
import { useSplitClient } from 'contrastBase/state'

export interface CellInfo {
  id: string
  name: string
  description: string
  builtIn: boolean
  index: number
}

const useOrgUuid = () => {
  const {
    parameters: [orgUuid]
  } = React.useContext(RouteContext)

  return {
    orgUuid
  }
}

const AgentLinkCell: React.FC<{
  orgId: string
  agentId: string
  agentName: string
}> = ({ orgId, agentId, agentName }) => {
  return (
    <Link href={getAgentUserDetailsRoute(orgId, agentId)}>{agentName}</Link>
  )
}

interface ApiKeysGridArgs {
  initialPageSize: number
  role?: Role
  checkRole: boolean
}

const DocumentationLink = (intl) => (msg) =>
  (
    <Link
      href={intl.formatMessage({
        id: 'link_configurationSettings'
      })}
      isExternal
    >
      {msg}
    </Link>
  )

const AgentFirstNameLink = ({
  agent,
  orgUuid
}: {
  agent: { uid: string; firstName: string }
  orgUuid: string
}) => {
  return (
    <AgentLinkCell
      agentId={agent.uid}
      orgId={orgUuid}
      agentName={agent.firstName}
    />
  )
}

const DeleteCell = ({
  agent,
  orgUuid,
  activeAgentUid,
  isOpen,
  openDialog,
  closeDialog,
  deleteAgentUser
}) => (
  <>
    <IconButton
      aria-label={'Delete'}
      testId={`delete-button-${agent.firstName}`}
      Icon={IconDelete}
      onClick={() => openDialog(agent.uid)}
    />

    {activeAgentUid === agent.uid && (
      <DeleteAgentDialog
        isOpen={isOpen}
        agentName={agent.firstName}
        onCancel={closeDialog}
        onConfirm={() => deleteAgentUser({ orgUuid, agentUid: agent.uid })}
      />
    )}
  </>
)

const AgentFirstNameCell =
  (orgUuid) =>
  ({ row: { original: agent } }) =>
    <AgentFirstNameLink agent={agent} orgUuid={orgUuid} />

const ActionsCell =
  ({
    orgUuid,
    activeAgentUid,
    isOpen,
    openDialog,
    closeDialog,
    deleteAgentUser,
    role,
    checkRole
  }) =>
  ({ row: { original: agent } }) => {
    return checkRole ? (
      <RoleSwitch
        role={role}
        requiredRole="ADMIN"
        renderAllowed={() => (
          <DeleteCell
            agent={agent}
            orgUuid={orgUuid}
            activeAgentUid={activeAgentUid}
            isOpen={isOpen}
            openDialog={openDialog}
            closeDialog={closeDialog}
            deleteAgentUser={deleteAgentUser}
          />
        )}
      />
    ) : (
      <DeleteCell
        agent={agent}
        orgUuid={orgUuid}
        activeAgentUid={activeAgentUid}
        isOpen={isOpen}
        openDialog={openDialog}
        closeDialog={closeDialog}
        deleteAgentUser={deleteAgentUser}
      />
    )
  }

// Taking src/components/rbac-table/rbac-table.tsx from Repo: platform-authz-mfe as reference
export const ApiKeysGrid = ({
  initialPageSize,
  role,
  checkRole
}: ApiKeysGridArgs) => {
  const { isSplitTreatmentOn } = useSplitClient()
  const isAgentUserLastSeenEnabled = isSplitTreatmentOn(
    'PROD-3545_agent_user_last_seen'
  )

  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [newAgentUserName, setNewAgentUserName] = useState<string>()
  const [isOpen, setIsOpen] = useState(false)
  const [activeAgentUid, setActiveAgentUid] = useState(null)

  const [isNewVisible, setIsNewVisible] = useState(false)
  const [hasNewInputError, setHasNewInputError] = useState(false)

  const intl = useIntl()

  const orgUuid = useOrgUuid().orgUuid

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize
  })

  const { pageIndex, pageSize } = pagination

  const { error, isLoading, data, refetch } = useOrganizationAgents({
    orgUuid,
    filterText: searchQuery,
    pageIndex: pageIndex * pageSize,
    pageSize
  })

  const openDialog = (agentUid) => {
    setIsOpen(true)
    setActiveAgentUid(agentUid)
  }
  const closeDialog = () => {
    setIsOpen(false)
    setActiveAgentUid(null)
  }

  const showOnError = (error) => {
    const errorMessagesMap = {
      'must match "^[A-Za-z0-9-]+$"': intl.formatMessage({
        id: 'detail_newAgentRequestError'
      })
    }

    // @ts-ignore
    const errorMessage =
      error?.data?.messages?.[0] ||
      // @ts-ignore
      error?.data?.errors
        ?.map((it) => {
          const customMessage = errorMessagesMap[it.message] || it.message
          return `${customMessage}`
        })
        .join(', ') ||
      intl.formatMessage({ id: 'detail_apiRequestError' })

    toast(errorMessage, { variant: 'error' })
  }

  const { mutate: addAgentUser } = useAddAgentUser({
    onSuccess: async (response) => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      await refetch()
      toast(intl.formatMessage({ id: 'title_newAgent' }))
      setIsNewVisible(false)
      queryClient.removeQueries(Query.AGENT_CONFIG)
      updateRoute(getAgentUserDetailsRoute(orgUuid, response?.agent.uid))
    },
    onError: (error) => {
      showOnError(error)
    }
  })

  const { mutate: deleteAgentUser } = useDeleteAgentUser({
    onSuccess: async () => {
      await refetch()
      toast(intl.formatMessage({ id: 'title_success' }))
      setIsOpen(false)
    },
    onError: (error) => {
      showOnError(error)
    }
  })

  const addAgentUserHandler = (orgUuid) => {
    if (!newAgentUserName?.trim()) {
      setHasNewInputError(true)
    } else {
      setHasNewInputError(false)
      addAgentUser({ orgUuid, agentName: newAgentUserName })
    }
  }

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0
    }))
  }, [searchQuery])

  const hasError = !!(error || data?.success === false)

  const totalResults = data?.total
  const pageCount = Math.ceil(
    totalResults && pageSize ? totalResults / pageSize : 0
  )

  const pageSizes: number[] = [initialPageSize, 20]

  const headerCss = cx(
    readOnlyGridCss,
    css`
      margin-bottom: 25px;
    `
  )

  const stackButtonStyle = css`
    padding-top: 10px;
  `
  const buttonStyle = css`
    padding: 12px 16px 12px;
  `

  const addAgentButton = (
    <Stack flexDirection="column" className={stackButtonStyle}>
      <Button
        testId="new-agent-button"
        onClick={() => setIsNewVisible(true)}
        Icon={IconAdd}
        className={buttonStyle}
        isDisabled={isNewVisible}
      >
        {intl.formatMessage({ id: 'action_addKeyname' })}
      </Button>
    </Stack>
  )

  const columns = (intl, orgUuid) => {
    const baseColumns = [
      {
        accessorKey: 'id',
        header: intl.formatMessage({ id: 'title_keyname' }),
        cell: AgentFirstNameCell(orgUuid)
      },
      {
        accessorKey: 'signupDate',
        header: intl.formatMessage({ id: 'title_created' }),
        cell: (info: { getValue: () => number }) => {
          const created = info.getValue() * 1000
          return intl.formatDate(created, dateFormats.DATETIME_LONG)
        }
      }
    ]

    const lastSeenColumn = {
      accessorKey: 'lastLoginTime',
      header: intl.formatMessage({ id: 'title_lastSeen' }),
      cell: (info: { getValue: () => number }) => {
        const lastSeen = info.getValue() * 1000
        return lastSeen
          ? intl.formatDate(lastSeen, dateFormats.DATETIME_LONG)
          : ''
      }
    }

    const actionColumn = {
      accessorKey: 'actions',
      cell: ActionsCell({
        orgUuid,
        activeAgentUid,
        isOpen,
        openDialog,
        closeDialog,
        deleteAgentUser,
        role,
        checkRole
      })
    }

    return isAgentUserLastSeenEnabled
      ? [...baseColumns, lastSeenColumn, actionColumn]
      : [...baseColumns, actionColumn]
  }

  const addAgentSection = (
    <Stack justifyContent="space-between">
      <TextInput
        testId="key-name-input"
        value={newAgentUserName}
        onChange={(event) => {
          setNewAgentUserName(event.target.value)
        }}
        placeholder={intl.formatMessage({
          id: 'placeholder_enterKeyname'
        })}
        error={
          hasNewInputError
            ? intl.formatMessage({
                id: 'placeholder_enterKeyname'
              })
            : undefined
        }
      ></TextInput>

      <Stack flexDirection="column" className={stackButtonStyle}>
        <Button
          testId="cancel-button"
          onClick={() => {
            setNewAgentUserName('')
            setIsNewVisible(false)
          }}
          variant="secondary"
          className={buttonStyle}
        >
          <FormattedMessage id="action_cancel" />
        </Button>
      </Stack>

      <Stack flexDirection="column" className={stackButtonStyle}>
        <Button
          testId="add-button"
          onClick={() => {
            addAgentUserHandler(orgUuid)
          }}
          className={buttonStyle}
        >
          {intl.formatMessage({ id: 'action_saveKeyname' })}
        </Button>
      </Stack>
    </Stack>
  )

  return (
    <>
      <div className={headerCss}>
        <FormattedMessage
          id="detail_apiKeys_apiDocumentation"
          values={{
            a: DocumentationLink(intl)
          }}
        />
      </div>
      <Stack
        justifyContent="space-between"
        className={css`
          padding-bottom: 20px;
        `}
      >
        <SearchInput
          testId="search-input"
          onChange={(_, value) => setSearchQuery(value || '')}
          style={{ width: '300px' }}
          placeholder={intl.formatMessage({ id: 'placeholder_searchKeyname' })}
        />

        <Stack justifyContent="flex-end">
          {isNewVisible && addAgentSection}
          {checkRole
            ? role !== undefined && (
                <RoleSwitch
                  role={role}
                  requiredRole={'EDIT'}
                  renderAllowed={() => addAgentButton}
                />
              )
            : addAgentButton}
        </Stack>
      </Stack>

      <Table
        testId="multiple-agent-users-table"
        hasError={hasError}
        isLoading={isLoading}
        columns={columns(intl, orgUuid)}
        data={data?.agents || []}
        PaginationProps={{
          pageSizes,
          totalResults,
          showInputNav: true,
          allowFirstPageNav: true
        }}
        tableOptions={{
          manualPagination: true,
          onPaginationChange: (getPagination: () => PaginationState) => {
            const { pageIndex, pageSize } = getPagination() || {}
            if (pageSize) {
              setPagination({
                pageIndex,
                pageSize
              })
            }
          },
          pageCount,
          state: { pagination: { pageIndex, pageSize } }
        }}
        emptyStateProps={{
          headerText: null,
          bodyText: (
            <Text font="body" color="neutral70">
              <FormattedMessage
                id={'detail_noAgentUsersResults'}
                values={{
                  agentUser: searchQuery
                }}
              />
            </Text>
          )
        }}
      />
    </>
  )
}
